import type { Query } from "@cubejs-client/core";
import { err, isErr, ok, type Result } from "@hightouch/lib/util/result";
import { z } from "zod/v4";
import { useAdInsightsCubeLoadQuery } from "src/graphql/types";
import { captureException } from "src/lib/monitoring";
import type {
  CubeQueryFailure,
  CubeWire,
  NonEmptyArray,
  ResponseFailure,
} from "src/pages/ad-studio/insights/performance/types";
import { GraphQlError } from "src/utils/fetcher";

const rejectionSchema = z.union([
  z
    .instanceof(GraphQlError)
    .refine((error) => error.extensions.code === "INSIGHTS_NOT_BUILT")
    .transform((): ResponseFailure => ({ cause: "not-built" })),
  z
    .instanceof(GraphQlError)
    .transform((): ResponseFailure => ({ cause: "rejected" })),
]);

export const toResponseFailure = (error: unknown): ResponseFailure => {
  const rejection = rejectionSchema.safeParse(error);
  return rejection.success ? rejection.data : { cause: "network" };
};

const fetchLoadResponse = (
  query: Query | NonEmptyArray<Query>,
): Promise<Result<unknown, CubeQueryFailure>> =>
  useAdInsightsCubeLoadQuery
    .fetcher({ query })()
    .then(
      (data) => ok(data.adInsightsCubeQuery),
      (error) => {
        captureException(error);
        return err(toResponseFailure(error));
      },
    );

/** Parses a Cube query against meta, loads it, and reads the answer with the rows schema; every failure is a named cause. */
export async function loadCubeQuery<T>({
  query,
  rows,
  wire,
}: {
  query: Query;
  rows: z.ZodType<T, unknown>;
  wire: CubeWire;
}): Promise<Result<T, CubeQueryFailure>> {
  const parsed = wire.query.safeParse(query);
  if (!parsed.success) {
    return err({ cause: "invalid-query", issues: parsed.error.issues });
  }
  const loaded = await fetchLoadResponse(parsed.data);
  if (isErr(loaded)) {
    return loaded;
  }
  const response = wire.response(parsed.data).safeParse(loaded.value);
  if (!response.success) {
    return err({ cause: "bad-response", issues: response.error.issues });
  }
  const value = rows.safeParse(response.data.results[0]);
  return value.success
    ? ok(value.data)
    : err({ cause: "bad-response", issues: value.error.issues });
}

export async function loadCubeBlend<T>({
  queries,
  rows,
  wire,
}: {
  queries: NonEmptyArray<Query>;
  rows: z.ZodType<T, unknown>;
  wire: CubeWire;
}): Promise<Result<T, CubeQueryFailure>> {
  const parsed = z.tuple([wire.query], wire.query).safeParse(queries);
  if (!parsed.success) {
    return err({ cause: "invalid-query", issues: parsed.error.issues });
  }
  const echoed: NonEmptyArray<Query> = parsed.data;
  const loaded = await fetchLoadResponse(echoed);
  if (isErr(loaded)) {
    return loaded;
  }
  const response = wire.blendResponse(echoed).safeParse(loaded.value);
  if (!response.success) {
    return err({ cause: "bad-response", issues: response.error.issues });
  }
  const value = rows.safeParse(response.data.results);
  return value.success
    ? ok(value.data)
    : err({ cause: "bad-response", issues: value.error.issues });
}
